import React, { useState } from 'react';
import { X, Save, Eye, EyeOff } from 'lucide-react';

const inputCls = 'w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white outline-none transition-all';
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

const AgentModal = ({
    show,
    editingAgent,
    tools,
    selectedToolId,
    toolPrompt,
    additionalToolPrompt,
    agentForm,
    toolConfig,
    onClose,
    onSave,
    onAgentFormChange,
    onToolSelection,
    onToolPromptChange,
    onAdditionalToolPromptChange,
}) => {
    const [configValue, setConfigValue] = useState('');

    if (!show) return null;

    const renderToolConfigInput = () => {
        if (!toolConfig) return null;

        if (toolConfig.dropdown) {
            return (
                <div className="space-y-2">
                    <label className={labelCls}>{toolConfig.dropdown.label}</label>
                    <select
                        value={configValue}
                        onChange={(e) => setConfigValue(e.target.value)}
                        className={inputCls}
                    >
                        <option value="">Select an option</option>
                        {toolConfig.dropdown.options.map((option, index) => (
                            <option key={index} value={option}>{option}</option>
                        ))}
                    </select>
                    {configValue && (
                        <div className="space-y-2 mt-2">
                            <div>
                                <label className={labelCls}>URL</label>
                                <input type="text" className={inputCls} placeholder="Enter Database URL" />
                            </div>
                            <div>
                                <label className={labelCls}>Username</label>
                                <input type="text" className={inputCls} placeholder="Enter Database Username" />
                            </div>
                            <div>
                                <label className={labelCls}>Password</label>
                                <input type="text" className={inputCls} placeholder="Enter Database Password" />
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (toolConfig.input) {
            return (
                <div className="space-y-2">
                    <label className={labelCls}>{toolConfig.input.label}</label>
                    <input
                        type={toolConfig.input.type || 'text'}
                        value={configValue}
                        onChange={(e) => setConfigValue(e.target.value)}
                        placeholder={toolConfig.input.placeholder || ''}
                        className={inputCls}
                    />
                </div>
            );
        }

        return null;
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded shadow-xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h3 className="text-base font-semibold text-gray-800">
                        {editingAgent ? 'Edit Agent' : 'New Agent'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded transition-colors">
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
                    <div>
                        <label className={labelCls}>Agent Name</label>
                        <input
                            value={agentForm.name}
                            onChange={(e) => onAgentFormChange('name', e.target.value)}
                            className={inputCls}
                            placeholder="Enter agent name..."
                        />
                    </div>

                    {tools.length > 0 && (
                        <div>
                            <label className={labelCls}>Select Tool</label>
                            <div className="grid grid-cols-2 gap-2">
                                {tools.map((tool) => (
                                    <label
                                        key={tool.id}
                                        className={`p-3 border-2 rounded cursor-pointer transition-all ${
                                            selectedToolId === tool.id
                                                ? 'border-primary bg-primary/5'
                                                : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            value={tool.id}
                                            checked={selectedToolId === tool.id}
                                            onChange={() => onToolSelection(tool.id)}
                                            className="sr-only"
                                        />
                                        <div className="text-sm font-medium text-gray-800">{tool.description}</div>
                                        <div className="text-xs text-gray-400 mt-0.5">{tool.category}</div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {renderToolConfigInput()}

                    {selectedToolId && (
                        <div>
                            <label className={labelCls}>Tool Prompt</label>
                            <textarea
                                rows={3}
                                value={toolPrompt}
                                onChange={(e) => onToolPromptChange(e.target.value)}
                                className={`${inputCls} resize-none`}
                                placeholder="Enter tool-specific instructions..."
                            />
                        </div>
                    )}

                    {selectedToolId && selectedToolId !== '00002' && (
                        <div>
                            <label className={labelCls}>Visualization</label>
                            <div className="flex gap-3">
                                <label className={`flex items-center gap-2 px-3 py-2 rounded border-2 cursor-pointer transition-all ${agentForm.status === 'active' ? 'border-green-400 bg-green-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                                    <input
                                        type="radio"
                                        value="active"
                                        checked={agentForm.status === 'active'}
                                        onChange={(e) => onAgentFormChange('status', e.target.value)}
                                        className="sr-only"
                                    />
                                    <Eye className="w-4 h-4 text-green-500" />
                                    <span className="text-sm font-medium text-gray-700">Active</span>
                                </label>
                                <label className={`flex items-center gap-2 px-3 py-2 rounded border-2 cursor-pointer transition-all ${agentForm.status === 'inactive' ? 'border-gray-400 bg-gray-100' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                                    <input
                                        type="radio"
                                        value="inactive"
                                        checked={agentForm.status === 'inactive'}
                                        onChange={(e) => onAgentFormChange('status', e.target.value)}
                                        className="sr-only"
                                    />
                                    <EyeOff className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-700">Inactive</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {selectedToolId && selectedToolId !== '00002' && agentForm.status === 'active' && (
                        <div>
                            <label className={labelCls}>Visualization Prompt</label>
                            <textarea
                                rows={3}
                                value={additionalToolPrompt}
                                onChange={(e) => onAdditionalToolPromptChange(e.target.value)}
                                className={`${inputCls} resize-none`}
                                placeholder="Enter visualization instructions..."
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                    <button
                        onClick={onSave}
                        disabled={!agentForm.name || !selectedToolId}
                        className="px-5 py-2 bg-primary hover:bg-primaryDark disabled:bg-gray-200 disabled:cursor-not-allowed text-white text-sm rounded transition-colors flex items-center gap-1.5 font-medium"
                    >
                        <Save className="w-3.5 h-3.5" /> Save Agent
                    </button>
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-600 text-sm rounded transition-colors font-medium"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AgentModal;
